<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CalendarDays, ChevronRight, Menu, Trophy, Video, Youtube } from 'lucide-vue-next';
import { useRoles } from '@/helpers/roleHelpers';
import AppearanceTabs from '@/components/AppearanceTabs.vue';
import PlaceholderPattern from '@/components/PlaceholderPattern.vue';

// Récupération des props depuis le contrôleur
defineProps<{
    teamRanking: Array<{ rank: number, name: string, points: number, id: string }>,
    challengeRanking: Array<{ rank: number, name: string, completions: number, id: string }>,
}>();

// Données factices pour les actualités
const news = [
    {
        id: 1,
        title: 'Début des 24h étiq',
        content: 'Les 24h étiq ont commencé! Tous les participants sont prêts pour relever les défis.',
        date: '15 mai 2025 09:00',
    },
    {
        id: 2,
        title: 'L\'équipe Alpha en tête du classement',
        content: 'Après 6 heures de compétition, l\'équipe Alpha prend la tête avec une avance considérable.',
        date: '15 mai 2025 15:30',
    },
    {
        id: 3,
        title: 'Nouvelle épreuve disponible',
        content: 'Une nouvelle épreuve de cryptographie vient d\'être dévoilée. Les équipes ont 3 heures pour la résoudre.',
        date: '15 mai 2025 18:00',
    },
    {
        id: 4,
        title: 'Workshop sur la sécurité web',
        content: 'Un workshop sur les dernières vulnérabilités web sera organisé demain à 10h00.',
        date: '16 mai 2025 08:30',
    },
    {
        id: 5,
        title: 'Phase finale',
        content: 'Plus que 4 heures avant la fin de la compétition! La tension monte parmi les équipes.',
        date: '16 mai 2025 20:00',
    },
];

const { hasRole } = useRoles();
</script>

<template>
    <Head title="Accueil">
        <link rel="preconnect" href="https://rsms.me/" />
        <link rel="stylesheet" href="https://rsms.me/inter/inter.css" />
    </Head>

    <div class="min-h-screen bg-background">
        <!-- Header avec navigation -->
        <header class="border-b bg-card">
            <div class="container mx-auto max-w-7xl flex h-16 items-center justify-between px-4">
                <div class="flex items-center gap-6">
                    <Link href="/" class="flex items-center gap-2 font-bold text-xl">
                        <Trophy class="h-6 w-6" />
                        <span>24h de l'IUT</span>
                    </Link>
                </div>

                <div class="flex items-center gap-4">
                    <div v-if="$page.props.auth.user" class="flex items-center gap-4">
                        <Button v-if="hasRole('game-manager')" as-child class="hidden md:flex">
                            <Link :href="route('dashboard')">
                                Tableau de bord
                            </Link>
                        </Button>
                    </div>
                    <div v-else class="flex items-center gap-4">
                        <Button as-child class="hidden md:flex">
                            <Link :href="route('login')">
                                Connexion
                            </Link>
                        </Button>
                        <Button as-child class="hidden md:flex">
                            <Link :href="route('register')">
                                Inscription
                            </Link>
                        </Button>
                    </div>

                    <!-- Menu mobile -->
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon" class="md:hidden">
                                <Menu class="h-5 w-5" />
                                <span class="sr-only">Menu</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" class="w-48">
                            <DropdownMenuItem v-if="$page.props.auth.user" asChild>
                                <Link :href="route('dashboard')">Tableau de bord</Link>
                            </DropdownMenuItem>
                            <template v-else>
                                <DropdownMenuItem asChild>
                                    <Link :href="route('login')">Connexion</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link :href="route('register')">Inscription</Link>
                                </DropdownMenuItem>
                            </template>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>

        <main class="container mx-auto max-w-7xl px-4 pb-8 pt-4 md:pb-12 md:pt-6">
            <div class="mb-6 flex items-center justify-end">
                <AppearanceTabs />
            </div>

            <!-- Héro -->
            <section class="mb-12 mt-4 md:mt-6 grid gap-6 lg:grid-cols-2">
                <div class="space-y-4">
                    <h1 class="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                        Bienvenue aux 24h de l'IUT 2025
                    </h1>
                    <p class="text-lg text-muted-foreground">
                        Un challenge unique mêlant intelligence artificielle et développement web, ouvert aux étudiants de l'IUT de Dijon (et plus). Relevez les épreuves, cumulez des points et hissez-vous parmi les meilleurs !
                    </p>
                    <div class="flex flex-wrap gap-4">
                        <Button size="lg" asChild>
                            <Link href="#classements">Voir les classements</Link>
                        </Button>
                    </div>
                </div>
                <div class="relative aspect-video overflow-hidden rounded-xl border">
                    <!-- Embed YouTube -->
                    <div class="flex h-full items-center justify-center bg-muted">
                        <PlaceholderPattern />
                    </div>
                </div>
            </section>

            <!-- Classements -->
            <section id="classements" class="mb-12 scroll-mt-16">
                <h2 class="mb-6 text-2xl font-bold tracking-tight">Classements de l'épreuve Web</h2>
                <Tabs defaultValue="teams" class="w-full">
                    <TabsList class="mb-4">
                        <TabsTrigger value="teams">Classement des équipes</TabsTrigger>
                        <TabsTrigger value="challenges">Classement des épreuves</TabsTrigger>
                    </TabsList>
                    <TabsContent value="teams">
                        <Card>
                            <CardHeader>
                                <CardTitle>Top des équipes</CardTitle>
                                <CardDescription>Classement basé sur les points accumulés</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ScrollArea v-if="teamRanking.length" className="h-[400px]">
                                    <div class="space-y-4">
                                        <div v-for="team in teamRanking" :key="`team-${team.id}`" class="flex items-center justify-between">
                                            <div class="flex items-center gap-4">
                                                <div class="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                                                    <span class="text-sm font-medium">{{ team.rank }}</span>
                                                </div>
                                                <div>
                                                    <p class="text-sm font-medium">{{ team.name }}</p>
                                                </div>
                                            </div>
                                            <div class="flex items-center gap-2">
                                                <Trophy v-if="team.rank <= 3" class="h-4 w-4 text-amber-500" />
                                                <span class="font-semibold">{{ team.points }} pts</span>
                                            </div>
                                        </div>
                                    </div>
                                </ScrollArea>
                                <div v-else class="py-8 text-center">
                                    <p class="text-muted-foreground">Aucune équipe enregistrée pour le moment</p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    <TabsContent value="challenges">
                        <Card>
                            <CardHeader>
                                <CardTitle>Épreuves les plus réussies</CardTitle>
                                <CardDescription>Classement basé sur le nombre de validations</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ScrollArea v-if="challengeRanking.length" className="h-[400px]">
                                    <div class="space-y-4">
                                        <div v-for="challenge in challengeRanking" :key="`challenge-${challenge.id}`" class="flex items-center justify-between">
                                            <div class="flex items-center gap-4">
                                                <div class="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                                                    <span class="text-sm font-medium">{{ challenge.rank }}</span>
                                                </div>
                                                <div>
                                                    <p class="text-sm font-medium">{{ challenge.name }}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <span class="font-semibold">{{ challenge.completions }} validation{{ challenge.completions > 1 ? 's' : '' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </ScrollArea>
                                <div v-else class="py-8 text-center">
                                    <p class="text-muted-foreground">Aucune épreuve complétée pour le moment</p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </section>

            <!-- Actualités -->
        </main>

        <footer class="border-t py-6 md:py-8">
            <div class="container mx-auto max-w-7xl px-4">
                <div class="flex flex-col items-center justify-between gap-4 md:flex-row">
                    <div class="flex items-center gap-2">
                        <Trophy class="h-5 w-5" />
                        <span class="text-sm font-medium">24h de l'IUT © 2025</span>
                    </div>
                </div>
            </div>
        </footer>
    </div>
</template>
